import { isCodeReferencedPrompt } from '@atlas/types'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import type { Prompt } from '@/domain/prompt.types'

/**
 * Confirm deleting a prompt.
 *
 * Wraps the app's one confirmation surface rather than building a dialog, so a
 * destructive prompt action looks and behaves like every other one.
 *
 * `confirmDisabled` carries the precondition: a prompt whose `code` live code
 * resolves by cannot be deleted. That is refused server-side, and the control is
 * disabled here from the SAME predicate — so the button and the guard can never
 * disagree about what is protected, and the reason is visible before anyone tries.
 */
export function DeletePromptDialog({
	prompt,
	open,
	isDeleting,
	error,
	onConfirm,
	onCancel,
}: {
	prompt: Prompt
	open: boolean
	isDeleting: boolean
	/** The server's refusal, which carries the reason worth reading. */
	error: string | null
	onConfirm: () => void
	onCancel: () => void
}) {
	const isProtected = isCodeReferencedPrompt(prompt.code)

	return (
		<ConfirmDialog
			open={open}
			title={`Delete “${prompt.name}”?`}
			description={
				isProtected
					? 'This prompt is resolved by name in code, so deleting it would break the caller that depends on it. Archive it instead, or remove the code reference first.'
					: 'Its published versions, test cases and audit trail are kept — the prompt stops appearing in the library and stops resolving for new work.'
			}
			confirmLabel="Delete prompt"
			busyLabel="Deleting…"
			confirmVariant="danger"
			confirmDisabled={isProtected}
			submitting={isDeleting}
			errorMessage={error}
			onConfirm={onConfirm}
			onCancel={onCancel}
		/>
	)
}
